import { useCallback, useEffect, useRef, useState } from "react";

import { IPatientDataChangedNotification } from "../@types/SignalR";
import { signalRService } from "../services/signalRService";

interface IUseSignalROptions {
  patientId?: string | number;
  onDataChanged?: (notification: IPatientDataChangedNotification) => void;
  enabled?: boolean;
}

export function useSignalR({
  patientId,
  onDataChanged,
  enabled = true,
}: IUseSignalROptions) {
  const [isConnected, setIsConnected] = useState(false);
  const onDataChangedRef = useRef(onDataChanged);

  useEffect(() => {
    onDataChangedRef.current = onDataChanged;
  }, [onDataChanged]);

  useEffect(() => {
    if (!enabled || !patientId) {
      return undefined;
    }

    const numericPatientId =
      typeof patientId === "string" ? parseInt(patientId, 10) : patientId;

    if (Number.isNaN(numericPatientId)) {
      return undefined;
    }

    const connectAndJoin = async () => {
      await signalRService.connect();
      setIsConnected(signalRService.isConnected());
      await signalRService.joinPatientGroup(numericPatientId);
    };

    connectAndJoin();

    const unsubscribe = signalRService.onPatientDataChanged((notification) => {
      if (
        notification.patientId === numericPatientId &&
        onDataChangedRef.current
      ) {
        onDataChangedRef.current(notification);
      }
    });

    return () => {
      unsubscribe();
      signalRService.leavePatientGroup(numericPatientId);
    };
  }, [patientId, enabled]);

  const reconnect = useCallback(async () => {
    await signalRService.disconnect();
    await signalRService.connect();
    setIsConnected(signalRService.isConnected());

    if (patientId) {
      const numericPatientId =
        typeof patientId === "string" ? parseInt(patientId, 10) : patientId;
      if (!Number.isNaN(numericPatientId)) {
        await signalRService.joinPatientGroup(numericPatientId);
      }
    }
  }, [patientId]);

  return {
    isConnected,
    reconnect,
  };
}
